import React, { useEffect, useRef, useState } from 'react';

const APP_STORE_URL = 'https://apps.apple.com/us/app/manso/id6760553408';
const DEMO_VIDEOS = {
  closet: '/assets/digital-closet-demo.mp4',
  stylist: '/assets/try-it-on-demo.mp4',
  planner: '/assets/calendar-demo.mp4',
  shopping: '/assets/shop-demo.mp4',
  analytics: '/assets/analytics-demo.mp4',
  community: '/assets/discover-demo.mp4',
};

const AppleLogo = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M16.365 1.43c0 1.14-.466 2.23-1.215 2.998-.81.831-2.133 1.47-3.304 1.377-.148-1.087.436-2.243 1.176-2.993.811-.84 2.216-1.44 3.343-1.382Zm4.81 16.997c-.574 1.309-.84 1.892-1.58 3.04-1.033 1.602-2.492 3.598-4.302 3.613-1.607.014-2.02-1.058-4.2-1.046-2.18.013-2.634 1.066-4.24 1.052-1.81-.016-3.191-1.812-4.225-3.414-2.888-4.473-3.19-9.72-1.41-12.46 1.266-1.95 3.27-3.094 5.153-3.094 1.92 0 3.127 1.06 4.713 1.06 1.538 0 2.476-1.062 4.699-1.062 1.677 0 3.453.914 4.718 2.494-4.145 2.275-3.472 8.24.674 9.817Z" />
  </svg>
);

const brandLogos = [
  'Gucci',
  'Prada',
  'Versace',
  'Fendi',
  'Bottega Veneta',
  'Valentino',
  'Dolce & Gabbana',
  'Miu Miu',
  'Armani',
  'Ferragamo',
  'Nike',
  'Adidas',
  'Givenchy',
  'Patagonia',
];

const BrandMark = ({ name }) => (
  <div className="manso-brand-mark" aria-label={name}>
    {name}
  </div>
);

const featureScreens = {
  closet: {
    icon: '👗',
    title: 'Digital Closet',
    kicker: 'My Closet',
    copyTitle: 'A living closet in your pocket',
    copyText: 'Catalog every piece, search fast, and let AI tags keep your wardrobe organized by color, category, season, and brand.',
    note: 'Auto-tagged pieces stay searchable in seconds.',
    items: [
      ['Oxford', 'Cream Oxford Shirt', 'Tops · Workwear · Worn 8 times'],
      ['Blazer', 'Navy Wool Blazer', 'Outerwear · 94% versatile'],
      ['Loafer', 'Leather Loafers', 'Shoes · Cost per wear tracked'],
    ],
  },
  stylist: {
    icon: '✨',
    title: "Today's Outfit",
    kicker: 'AI Stylist',
    copyTitle: 'Recommendations that know your closet',
    copyText: 'Preview outfit ideas with style match, occasion logic, and alternatives built from pieces the user already owns.',
    note: 'Weather, plans, and taste profile are folded into every outfit.',
    items: [
      ['94%', 'Style Match', 'Navy blazer + cream shirt + charcoal trouser'],
      ['Dinner', 'Business Casual', 'Polished, relaxed, and weather-aware'],
      ['Swap', 'Alternative Look', 'Chelsea boots instead of loafers'],
    ],
  },
  planner: {
    icon: '📅',
    title: 'This Week',
    kicker: 'Outfit Planner',
    copyTitle: 'Plan looks before the week starts',
    copyText: 'Turn saved outfits into a visual calendar so users can schedule looks, avoid accidental repeats, and stay ready.',
    note: 'Saved outfits can be dropped into days and occasions.',
    items: [
      ['Mon', 'Client Meeting', 'Camel coat, white knit, tailored trouser'],
      ['Thu', 'Dinner', 'Silk shirt, dark denim, loafers'],
      ['Sat', 'Weekend', 'Relaxed blazer, ribbed tank, sneakers'],
    ],
  },
  shopping: {
    icon: '🛍️',
    title: 'Smart Gaps',
    kicker: 'Shopping',
    copyTitle: 'Buy what completes the wardrobe',
    copyText: 'Partner brand suggestions become useful because they are scored by how many outfits they unlock from the current closet.',
    note: 'Each recommendation connects back to pieces already owned.',
    items: [
      ['Need', 'Black Belt', 'Unlocks 14 outfits · 6 partner brands'],
      ['Pair', 'Fine Knit Polo', 'Pairs with 9 saved bottoms'],
      ['Add', 'Minimal Sneaker', 'Fills casual weekend gap'],
    ],
  },
  analytics: {
    icon: '📊',
    title: 'Wear Analytics',
    kicker: 'Insights',
    copyTitle: 'Know what earns its place',
    copyText: 'See cost per wear, neglected pieces, and the wardrobe staples worth repeating more often.',
    note: 'Insights help users buy less and wear more of what they own.',
    items: [
      ['18x', 'Most Worn', 'Cream Oxford · $4.12 per wear'],
      ['42d', 'Needs Rotation', 'Linen blazer has been resting'],
      ['31', 'Best Value', 'Black denim outfits created'],
    ],
  },
  community: {
    icon: '👥',
    title: 'Friends & Looks',
    kicker: 'Community',
    copyTitle: 'Style inspiration from people you know',
    copyText: 'Browse friends’ wardrobes, see the looks they save, and discover new outfit ideas through a social feed built around real closets.',
    note: 'Follow friends, save looks, and turn real wardrobes into daily inspiration.',
    items: [
      ['Ava', 'Saved Look', 'Black blazer, slip skirt, kitten heels'],
      ['Noah', 'Closet Peek', 'Minimal capsule pieces trending now'],
      ['Feed', 'Community Picks', '12 friend looks to inspire tomorrow'],
    ],
  },
};

const features = [
  ['closet', 'Digital Closet', 'Photograph, tag, and organize every piece you own, searchable in seconds.'],
  ['stylist', 'AI Stylist', 'Daily outfit suggestions based on weather, occasion, and your personal taste profile.'],
  ['planner', 'Outfit Planner', "Plan your week's looks in advance and avoid repeating an outfit unintentionally."],
  ['shopping', 'Smart Shopping', 'Find pieces from partner brands that actually match what you own.'],
  ['analytics', 'Wear Analytics', 'See which items you wear most and which pieces deserve another moment.'],
  ['community', 'Social & Community', "Explore friends' wardrobes and saved looks to get inspired by people whose style you trust."],
];

const scrollyFeatures = [
  {
    key: 'closet',
    label: 'Digital Closet',
    title: <>A living closet<br /><em>in your pocket</em></>,
    description: 'Photograph, tag, and organize every piece you own. Search by category, color, brand, or season and find what you need in seconds.',
    tag: 'Real app demo',
    videoSrc: DEMO_VIDEOS.closet,
  },
  {
    key: 'stylist',
    label: 'AI Stylist',
    title: <>Try it on before<br /><em>you step out</em></>,
    description: "See exactly how an outfit looks before you step out. Manso's Virtual Try-On is generated from your wardrobe and tailored to your body and taste.",
    tag: 'Real app screen',
    videoSrc: DEMO_VIDEOS.stylist,
  },
  {
    key: 'planner',
    label: 'Outfit Planner',
    title: <>Plan looks<br /><em>before the week starts</em></>,
    description: 'Schedule looks ahead of time, avoid repeats, and keep your wardrobe ready for work, weekends, travel, and events.',
    tag: 'Real app demo',
    videoSrc: DEMO_VIDEOS.planner,
  },
  {
    key: 'shopping',
    label: 'Smart Shopping',
    title: <>Buy what complements<br /><em>your wardrobe</em></>,
    description: 'Find pieces that actually complete your wardrobe, scored by how well they match what you already own.',
    tag: 'Real app demo',
    videoSrc: DEMO_VIDEOS.shopping,
  },
  {
    key: 'analytics',
    label: 'Wear Analytics',
    title: <>The numbers behind<br /><em>your style</em></>,
    description: 'See what you wear most, what is collecting dust, and which pieces earn their place over time.',
    tag: 'Real app demo',
    videoSrc: DEMO_VIDEOS.analytics,
  },
  {
    key: 'community',
    label: 'Social & Community',
    title: <>Style inspiration<br /><em>from people you know</em></>,
    description: "Explore friends' wardrobes and saved looks to discover outfit ideas grounded in pieces people actually own.",
    tag: 'Real app demo',
    videoSrc: DEMO_VIDEOS.community,
  },
];

const faqs = [
  ['What makes MANSO different from other styling apps?', 'MANSO combines advanced computer vision, personalized AI recommendations, and intelligent wardrobe analytics. Our platform learns your unique style, considers real-world factors like weather and events, and helps you maximize your existing wardrobe while making smarter shopping decisions.'],
  ['Is MANSO designed for both men and women?', "Absolutely! MANSO is designed to work seamlessly for all genders. Our AI technology is trained to recognize and categorize clothing items for everyone, and our styling recommendations are tailored and personalized specifically for each individual's unique style preferences, body type, and lifestyle - regardless of gender."],
  ['How does the AI recommendation system work?', 'Our AI analyzes your wardrobe, personal style preferences, weather conditions, and calendar events to suggest perfect outfits. It learns from your choices over time, becoming more accurate and personalized with each interaction.'],
  ['Can I connect with friends and share outfit ideas?', 'Yes! MANSO includes social features that let you connect with fashion-forward friends, explore shared wardrobes, and get inspiration from our style community. You can share your favorite looks and discover trending styles from other users.'],
  ['Is my wardrobe data secure and private?', 'Absolutely. We use enterprise-grade encryption to protect your data. Your wardrobe information is stored securely and never shared with third parties. You have complete control over your data and can delete it at any time.'],
];

const LandingPage = () => {
  const [activeFeature, setActiveFeature] = useState('closet');
  const [activeScrolly, setActiveScrolly] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [scrollyPhoneState, setScrollyPhoneState] = useState({ mode: 'before', left: 0 });
  const scrollySectionRef = useRef(null);
  const scrollyPhoneWrapRef = useRef(null);
  const scrollyRefs = useRef([]);
  const scrollyVideoRefs = useRef([]);
  const scrollyProgressRefs = useRef([]);
  const logoLoop = [...brandLogos, ...brandLogos];
  const logoLoopOffset = [...brandLogos.slice(5), ...brandLogos.slice(0, 5), ...brandLogos.slice(5), ...brandLogos.slice(0, 5)];

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    window.scrollTo(0, 0);

    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  const handleFeatureClick = key => {
    const nextIndex = scrollyFeatures.findIndex(feature => feature.key === key);
    setActiveFeature(key);
    setActiveScrolly(nextIndex);

    const target = scrollyRefs.current[nextIndex];
    if (target && window.__mansoLenis) {
      const offset = -(window.innerHeight - target.offsetHeight) / 2;
      window.__mansoLenis.scrollTo(target, { offset });
    } else if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || !window.Lenis) {
      return undefined;
    }

    const lenis = new window.Lenis({
      duration: 1.2,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 0.9,
      touchMultiplier: 1.4,
    });

    window.__mansoLenis = lenis;
    document.documentElement.classList.add('lenis', 'lenis-smooth');

    let frameId = 0;
    const raf = time => {
      lenis.raf(time);
      frameId = window.requestAnimationFrame(raf);
    };

    frameId = window.requestAnimationFrame(raf);

    return () => {
      window.cancelAnimationFrame(frameId);
      lenis.destroy();
      delete window.__mansoLenis;
      document.documentElement.classList.remove('lenis', 'lenis-smooth', 'lenis-stopped', 'lenis-scrolling');
    };
  }, []);

  useEffect(() => {
    const revealElements = Array.from(document.querySelectorAll('.manso-reveal'));

    if (!revealElements.length) {
      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      revealElements.forEach(element => element.classList.add('is-in-view'));
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in-view');
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.14,
    });

    revealElements.forEach(element => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const items = scrollyRefs.current.filter(Boolean);
    if (!items.length) return undefined;

    let frameId = 0;

    const updateActiveItem = () => {
      const viewportCenter = window.innerHeight / 2;
      let nextIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.top + rect.height / 2;
        const containsCenter = rect.top <= viewportCenter && rect.bottom >= viewportCenter;
        const distance = containsCenter ? 0 : Math.abs(itemCenter - viewportCenter);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nextIndex = index;
        }
      });

      setActiveScrolly(nextIndex);
      setActiveFeature(scrollyFeatures[nextIndex]?.key ?? 'closet');

      const section = scrollySectionRef.current;
      const phoneWrap = scrollyPhoneWrapRef.current;
      const isDesktop = window.matchMedia('(min-width: 641px)').matches;

      if (!section || !phoneWrap || !isDesktop) {
        setScrollyPhoneState({ mode: 'before', left: 0 });
        return;
      }

      const sectionRect = section.getBoundingClientRect();
      const wrapRect = phoneWrap.getBoundingClientRect();
      const nextMode = sectionRect.top <= 0 && sectionRect.bottom >= window.innerHeight
        ? 'fixed'
        : sectionRect.top > 0
          ? 'before'
          : 'after';

      setScrollyPhoneState({
        mode: nextMode,
        left: wrapRect.left + wrapRect.width / 2,
      });
    };

    const requestUpdate = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateActiveItem);
    };

    updateActiveItem();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, []);

  useEffect(() => {
    let progressFrameId = 0;
    const activeVideo = scrollyVideoRefs.current[activeScrolly];

    const setProgress = (index, progress) => {
      const fill = scrollyProgressRefs.current[index];
      if (fill) {
        fill.style.transform = `scaleX(${Math.max(0, Math.min(progress, 1))})`;
      }
    };

    scrollyVideoRefs.current.forEach((video, index) => {
      setProgress(index, 0);
      if (!video) return;

      if (index === activeScrolly) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });

    const updateProgress = () => {
      if (!activeVideo) return;

      const duration = activeVideo.duration;
      const progress = Number.isFinite(duration) && duration > 0
        ? activeVideo.currentTime / duration
        : 0;

      setProgress(activeScrolly, progress);
      progressFrameId = window.requestAnimationFrame(updateProgress);
    };

    updateProgress();
    activeVideo?.addEventListener('loadedmetadata', updateProgress);

    return () => {
      window.cancelAnimationFrame(progressFrameId);
      activeVideo?.removeEventListener('loadedmetadata', updateProgress);
    };
  }, [activeScrolly]);

  return (
    <main className="manso-redesign">
      <header className="manso-header">
        <a className="manso-header-brand" href="#top" aria-label="MANSO home">
          <img className="manso-header-logo" src="/assets/manso-logo.png" alt="" aria-hidden="true" />
          <span>MANSO</span>
        </a>
        <nav className="manso-header-actions" aria-label="Primary actions">
          <a className="manso-header-signin" href="https://app.manso.ai" target="_blank" rel="noreferrer">
            Sign in
          </a>
          <a className="manso-header-download" href={APP_STORE_URL} target="_blank" rel="noreferrer">
            <AppleLogo className="manso-apple" />
            Download App
          </a>
        </nav>
      </header>

      <section className="manso-hero">
        <div className="manso-hero-copy">
          <div className="manso-logo-tag">AI Wardrobe App &amp; Outfit Planner</div>
          <h1>
            Unlock your
            <br />
            <em>wardrobe’s full potential</em>
          </h1>
          <p>
            Your entire wardrobe, organized and styled by AI. Get outfit recommendations tailored
            to your taste, body, and occasion every single day.
          </p>
          <div className="manso-hero-actions">
            <a className="manso-btn manso-btn-primary" href={APP_STORE_URL} target="_blank" rel="noreferrer">
              <AppleLogo className="manso-apple" />
              Download App
            </a>
            <a className="manso-btn manso-btn-secondary" href="https://app.manso.ai" target="_blank" rel="noreferrer">
              Sign in
            </a>
          </div>
        </div>

        <div className="manso-hero-video-stack" aria-label="Manso style demo videos">
          <img className="manso-hero-video manso-hero-video-back" src="/assets/male-hero.png" alt="" aria-hidden="true" />
          <img className="manso-hero-video manso-hero-video-front" src="/assets/female-hero.png" alt="" aria-hidden="true" />
        </div>
      </section>

      <section className="manso-brands" aria-label="Brand partners">
        <div className="manso-brand-copy manso-reveal">
          <div className="manso-brand-label">
            Partnered with over <span>2,500</span> luxury brands including
          </div>
        </div>
        <div className="manso-brand-track-wrap">
          <div className="manso-brand-track manso-brand-track-primary">
            {logoLoop.map((name, index) => (
              <div className="manso-brand-logo" key={`${name}-${index}`}>
                <BrandMark name={name} />
              </div>
            ))}
          </div>
          <div className="manso-brand-track manso-brand-track-secondary" aria-hidden="true">
            {logoLoopOffset.map((name, index) => (
              <div className="manso-brand-logo" key={`${name}-offset-${index}`}>
                <BrandMark name={name} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="manso-features">
        <div className="manso-section-eyebrow manso-reveal">Core Features</div>
        <h2 className="manso-section-title manso-reveal manso-reveal-delay-1">Everything your wardrobe deserves</h2>

        <div className="manso-feature-grid manso-reveal manso-reveal-delay-2">
          {features.map(([key, title, description]) => (
            <button
              className={`manso-feature-item ${activeFeature === key ? 'active' : ''}`}
              key={key}
              type="button"
              onClick={() => handleFeatureClick(key)}
              aria-label={`View ${title} walkthrough`}
            >
              <div className="manso-feature-icon">{featureScreens[key].icon}</div>
              <div className="manso-feature-title">{title}</div>
              <div className="manso-feature-desc">{description}</div>
            </button>
          ))}
        </div>
      </section>

      <section
        id="feature-walkthrough"
        className="manso-scrolly-section"
        aria-label="Feature walkthrough"
        ref={scrollySectionRef}
      >
        <div className="manso-scrolly-intro manso-reveal">
          <div className="manso-scrolly-bridge">See each feature in motion</div>
        </div>

        <div id="feature-walkthrough-body" className="manso-scrolly-body">
          <div className="manso-scrolly-list">
            {scrollyFeatures.map((feature, index) => (
              <article
                className={`manso-scrolly-item ${activeScrolly === index ? 'active' : ''}`}
                data-index={index}
                key={feature.key}
                ref={element => {
                  scrollyRefs.current[index] = element;
                }}
              >
                <div className="manso-scrolly-num">{String(index + 1).padStart(2, '0')} — {feature.label}</div>
                <h3 className="manso-scrolly-title">{feature.title}</h3>
                <p className="manso-scrolly-desc">{feature.description}</p>
                <div className="manso-scrolly-tag">✦ {feature.tag}</div>
                <div className="manso-scrolly-progress-bar">
                  <div
                    className="manso-scrolly-progress-fill"
                    ref={element => {
                      scrollyProgressRefs.current[index] = element;
                    }}
                  />
                </div>
              </article>
            ))}
          </div>

          <div className="manso-scrolly-phone-wrap" ref={scrollyPhoneWrapRef}>
            <div
              className={`manso-scrolly-phone manso-scrolly-phone-${scrollyPhoneState.mode} ${activeScrolly >= 0 ? 'glow' : ''}`}
              style={{ '--manso-scrolly-phone-left': `${scrollyPhoneState.left}px` }}
            >
              <div className="manso-scrolly-phone-notch" />
              <div className="manso-scrolly-screen">
                {scrollyFeatures.map((feature, index) => (
                  <div className={`manso-scrolly-panel ${activeScrolly === index ? 'visible' : ''}`} key={feature.key}>
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      className="manso-scrolly-video"
                      aria-label={`${feature.label} demo video`}
                      ref={element => {
                        scrollyVideoRefs.current[index] = element;
                      }}
                    >
                      <source src={feature.videoSrc} type="video/mp4" />
                    </video>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="manso-faq">
        <div className="manso-faq-inner">
          <div className="manso-section-eyebrow manso-reveal">Questions</div>
          <h2 className="manso-section-title manso-reveal manso-reveal-delay-1">Everything to know before you start</h2>
          <div className="manso-faq-list">
            {faqs.map(([question, answer], index) => (
              <div className={`manso-faq-item manso-reveal manso-reveal-delay-${Math.min(index + 1, 3)} ${openFaq === index ? 'open' : ''}`} key={question}>
                <button
                  className="manso-faq-question"
                  type="button"
                  aria-expanded={openFaq === index}
                  aria-controls={`manso-faq-answer-${index}`}
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  {question}
                </button>
                <div
                  className="manso-faq-answer"
                  id={`manso-faq-answer-${index}`}
                  aria-hidden={openFaq !== index}
                >
                  <p>{answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="manso-cta">
        <h2 className="manso-reveal">
          Ready to elevate
          <br />
          <em>your wardrobe?</em>
        </h2>
        <p className="manso-reveal manso-reveal-delay-1">Organize your wardrobe, plan better outfits, and get dressed with more intention.</p>
        <div className="manso-cta-actions manso-reveal manso-reveal-delay-2">
          <a className="manso-btn manso-btn-primary" href={APP_STORE_URL} target="_blank" rel="noreferrer">
            <AppleLogo className="manso-apple" />
            Download App
          </a>
          <a className="manso-btn manso-btn-secondary" href="https://app.manso.ai" target="_blank" rel="noreferrer">
            Sign in
          </a>
        </div>
      </section>

      <footer className="manso-footer">
        <div className="manso-footer-brand">MANSO</div>
        <div className="manso-footer-right">
          <div className="manso-footer-copy">© 2026 Saturn Inc. All rights reserved.</div>
          <nav className="manso-footer-links" aria-label="Legal links">
            <a href="https://app.manso.ai/legal/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
            <a href="https://app.manso.ai/legal/terms" target="_blank" rel="noreferrer">Terms of Service</a>
          </nav>
        </div>
      </footer>
    </main>
  );
};

export default LandingPage;
